// Refunds (lane D2; field note register.dialog.refund; refunds parity §2–§4; target model §1.3): the refund aggregate of
// @radish/core/payments on the wire (stream `refund:<id>`). The register requests a refund (the payments and items it
// gives back, decided against the order's tenders and earlier refunds), refunds each card through the terminal and
// records each payment's outcome; the refund completes once every payment has one, with what was issued and what failed
// (RQ1 FIX: legacy recorded nothing when a later card failed). Payloads decode to exactly core's `RefundEvent` shapes.
import { z } from 'zod';
import { SETTLE_CHECKS } from '@radish/core/payments/settle-checks';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { EmployeeId, EpochMs, LocalDate, Milli, Money, OrderId, OrderLineId, RefundId, TenderId, TillSessionId, UserId } from '../../primitives/codecs.ts';

// `gift_card` (TenderStarted v4) is listed so a refund names any tender's method; core offers no gift-card refund until the ledger credits the card back (L17).
const TenderMethod = z.enum(['cash', 'card', 'other', 'gift_card']);

/**
 * B1912: how the register's screens name a payment of an order the node holds, as they name their own (core
 * `receiptTenderLabel`, legacy `buildTenderScreenLabel`): its legacy type, the name ("Visa", "Check", "DoorDash") and the
 * detail ("x4242", "#1234", ""), and the drawer ticket ("A-42"). Each is absent from a node before 2026-11-01, and an
 * older register drops them; the register then names the payment by `label` and `last4`, as before.
 */
const PaymentScreenName = {
  kind: z.enum(['cash', 'card', 'check', 'gift', 'house', 'other']).optional(),
  name: z.string().max(255).optional(),
  detail: z.string().max(260).optional(),
  ticket: z.string().max(255).optional(),
};
/** `full`: Refund Order; `by_line`: Refund Items; `custom_amount`: a custom amount; `tips_only`: Refund/Adjust Tip. */
export const RefundMode = z.enum(['by_line', 'custom_amount', 'tips_only', 'full']).meta({ id: 'RefundMode' });

const RefundTender = z.object({ tenderId: TenderId, amount: Money, tip: Money });
const RefundLine = z.object({ lineId: OrderLineId, qtyMilli: Milli, net: Money, tax: Money });

export const RefundSnapshot = z
  .object({
    refundId: RefundId,
    orderId: OrderId,
    status: z.enum(['requested', 'completed', 'failed']),
    mode: RefundMode,
    refundBusinessDate: LocalDate,
    tenders: z.array(RefundTender.extend({ method: TenderMethod, outcome: z.enum(['issued', 'failed']).nullable() })),
    lines: z.array(RefundLine),
    version: z.int().nonnegative(),
  })
  .meta({ id: 'RefundSnapshot' });

// ---- errors ----------------------------------------------------------------------------------------------------------
/** A refund core refuses (`rule` is core's error code, e.g. `refund_exceeds_captured`, `void_instead`). */
export const RefundRuleViolation = defineError({
  code: 'payments.refund_refused', httpStatus: 409, message: 'errors.payments.refund_refused', retryable: false,
  details: z.object({
    rule: z.string(),
    tenderId: TenderId.optional(),
    /** `leg_settled_by_hand`: what the hand settle recorded on the leg (review of #2594, S-1). */
    settled: z.enum(['issued', 'failed']).optional(),
  }),
});

// ---- events ----------------------------------------------------------------------------------------------------------
const refund = { aggregate: 'refund', deviceConsumed: true } as const;

/** RefundRequested v1 for a register on an older build: the same refund without the hand-back's rounding. */
function omitChangeRounding(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'changeRounding'));
}

export const RefundRequested = defineEvent({
  ...refund,
  name: 'RefundRequested',
  version: 2,
  summary: 'A refund of a closed order was requested: the payments and items it gives back, on the refund\'s own business date (L6).',
  payload: z.object({
    refundId: RefundId,
    orderId: OrderId,
    mode: RefundMode,
    tenders: z.array(RefundTender.extend({ method: TenderMethod })).min(1).max(20),
    lines: z.array(RefundLine).max(500),
    tillSessionId: TillSessionId,
    refundBusinessDate: LocalDate,
    requestedAt: EpochMs,
    reasonText: z.string().max(500).optional(),
    requestedByEmployeeId: EmployeeId.optional(),
    approvedByEmployeeId: EmployeeId.optional(),
    /**
     * v2: the cash change rounding of what the drawer handed back (exact − handed back, −4…+4¢), counted in the
     * drawer's cash rounding (legacy RecordRefundCashRounding on the refund's drawer). Absent: handed back exact.
     */
    changeRounding: Money.optional(),
  }),
  verifiable: ['orderId', 'mode', 'tenders', 'lines', 'refundBusinessDate', 'changeRounding'],
  merge: 'lifecycle',
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => omitChangeRounding(newer) }],
});

/**
 * Ops plan 2026-10-29-settle-unconfirmed-refunds (A212, L1044): a card refund whose answer never came and that Check
 * Again can't settle is settled by hand, after the person checked the processor's portal. Who did it, and the failure
 * the last Check Again saw. Absent: the processor (or the drawer) answered, as before.
 */
export const RefundSettlement = z
  .object({
    actor: z.discriminatedUnion('kind', [
      /** A manager at the register (the approving manager when the employee could not). */
      z.object({ kind: z.literal('employee'), employeeId: EmployeeId }),
      /** Radish staff or the location's manager, from the back office (an online refund). */
      z.object({ kind: z.literal('user'), userId: UserId }),
    ]),
    /** The error code of the last Check Again before the settle (e.g. `payments.card_operation_needs_review`). */
    lastCheck: z.string().min(1).max(64),
  })
  .meta({ id: 'RefundSettlement' });

/** v1 of a refund leg's outcome, for a reader on an older build: the same outcome without who settled it by hand. */
function omitSettledBy(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'settledBy'));
}

export const RefundTenderIssued = defineEvent({
  ...refund,
  name: 'RefundTenderIssued',
  version: 2,
  summary: 'One payment of a refund went back (a card refunded by the processor, cash handed back), or was settled as refunded by hand.',
  payload: z.object({
    tenderId: TenderId, refundTenderId: z.string().min(1).max(64), method: TenderMethod, amount: Money, tip: Money, gatewayRef: z.string().max(128).optional(),
    /** v2: set when a person settled the leg by hand after Check Again could not; the processor's reference is in `gatewayRef`. */
    settledBy: RefundSettlement.optional(),
  }),
  verifiable: ['tenderId', 'amount', 'tip'],
  merge: 'lifecycle',
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => omitSettledBy(newer) }],
});

export const RefundTenderFailed = defineEvent({
  ...refund,
  name: 'RefundTenderFailed',
  version: 2,
  summary: 'The processor refused one payment of a refund, or a person settled it as not refunded by hand.',
  payload: z.object({
    tenderId: TenderId, error: z.string().min(1).max(500),
    /** v2: set when a person settled the leg as not refunded by hand after Check Again could not. */
    settledBy: RefundSettlement.optional(),
  }),
  verifiable: ['tenderId'],
  merge: 'lifecycle',
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => omitSettledBy(newer) }],
});

export const RefundCompleted = defineEvent({
  ...refund,
  name: 'RefundCompleted',
  version: 1,
  summary: 'Every payment of a refund has an outcome: what was issued, and how many payments failed.',
  payload: z.object({ issuedAmount: Money, issuedTip: Money, failedTenders: z.int().nonnegative() }),
  verifiable: ['issuedAmount', 'issuedTip', 'failedTenders'],
  merge: 'lifecycle',
});

/** Every event the refund aggregate records, in the order core's `RefundEvent` union lists them. */
export const refundEvents = [RefundRequested, RefundTenderIssued, RefundTenderFailed, RefundCompleted] as const;

// ---- commands --------------------------------------------------------------------------------------------------------
const onRefund = {
  availability: 'location', origin: 'device', stream: { aggregate: 'refund', id: (input: { refundId: string }) => input.refundId },
  permission: { session: 'staff' }, output: RefundSnapshot, agent: { expose: false },
} as const;

export const RequestRefund = defineCommand({
  ...onRefund,
  name: 'payments.requestRefund',
  risk: 'medium',
  dryRun: true,
  summary: 'Refund a closed order: the whole order, chosen items, a custom amount or tips, to the payments that took it. Needs the void permission.',
  input: z.object({
    refundId: RefundId,
    orderId: OrderId,
    mode: RefundMode,
    tenders: z.array(RefundTender).min(1).max(20),
    lines: z.array(RefundLine).max(500),
    tillSessionId: TillSessionId,
    reasonText: z.string().trim().min(1).max(500).optional(),
    /** The cash change rounding of the hand-back (core `cashHandBack`), when the location rounds it. */
    changeRounding: Money.optional(),
  }),
  emits: [RefundRequested],
  errors: [RefundRuleViolation],
  legacy: ['POST /v1/refunds/{order_id}', 'POST /v1/order_refund/{order_id}'],
});

export const RecordRefundTenderResult = defineCommand({
  ...onRefund,
  name: 'payments.recordRefundTenderResult',
  risk: 'low',
  dryRun: false,
  summary: 'Record what the processor (or the drawer) did with one payment of a refund.',
  input: z.object({
    refundId: RefundId,
    tenderId: TenderId,
    outcome: z.discriminatedUnion('issued', [
      z.object({ issued: z.literal(true), refundTenderId: z.string().min(1).max(64), gatewayRef: z.string().min(1).max(128).optional() }),
      z.object({ issued: z.literal(false), error: z.string().min(1).max(500) }),
    ]),
    /**
     * Ops plan 2026-10-29-settle-unconfirmed-refunds, step 3: the answer is a person's hand settle made elsewhere (an
     * online refund settled in the back office, which `online.refundSale` names), copied onto the leg's outcome.
     */
    settledBy: RefundSettlement.optional(),
  }),
  emits: [RefundTenderIssued, RefundTenderFailed, RefundCompleted],
  errors: [RefundRuleViolation],
});

/**
 * Ops plan 2026-10-29-settle-unconfirmed-refunds (Andrew, A212: "Yes, manager settles"; L1044): a card payment of a
 * refund whose processor answer never came, and that Check Again could not settle, settled by hand after the person
 * checked the processor's portal. Refunded needs the processor's reference. It records the same outcome events as an
 * answer would, marked `settledBy`, so the refund folds and counts as any other.
 */
const SettleRefundTenderInput = z.object({
  refundId: RefundId,
  tenderId: TenderId,
  settlement: z.discriminatedUnion('issued', [
    z.object({ issued: z.literal(true), refundTenderId: z.string().min(1).max(64), gatewayRef: z.string().trim().min(1).max(128) }),
    z.object({ issued: z.literal(false) }),
  ]),
  /**
   * The error code of the last Check Again, which the register saw fail (e.g. `payments.card_operation_needs_review`).
   * Core refuses Not refunded after a failure that may only mean the first try is still running (review of #2594).
   */
  lastCheck: z.enum(SETTLE_CHECKS),
});

export const SettleRefundTender = defineCommand({
  ...onRefund,
  name: 'payments.settleRefundTender',
  risk: 'high',
  dryRun: true,
  summary: "Settle by hand a card payment of a refund whose processor answer never came and that Check Again could not settle: Refunded (with the processor's reference) or Not refunded. Needs the void permission.",
  input: SettleRefundTenderInput,
  emits: [RefundTenderIssued, RefundTenderFailed, RefundCompleted],
  errors: [RefundRuleViolation],
});

// ---- an order the register does not hold, through its node ------------------------------------------------------------
// Ops plan 2026-10-13-refund-imported-orders (lane D2): an order the legacy import brought over, or one rung on another
// register, lives on the node and not on this register. The register asks the node what the order can still give back,
// runs the same Refund wizard, and the node decides the refund (the register cannot: it holds neither the order nor its
// tenders). The register's link to its node is its device session, so the acting employee rides in the input, as the
// other register commands through the node do; the node checks that employee's own grants (the approving manager's when
// one approved), not the device's word. Card payments still go back through the cloud (`payments.runCardOperation`).

/** A payment of an earlier refund still waiting for the processor's answer: its card refund's answer was lost. */
const UnconfirmedRefundLeg = z.object({
  refundId: RefundId, tenderId: TenderId, method: TenderMethod, amount: Money, tip: Money,
  /** When its refund was asked for, so a register shows the Settle dialog's "Sent" for an order the node holds (#2622 review). */
  requestedAt: EpochMs.optional(),
});

export const RefundableOrder = z
  .object({
    orderId: OrderId,
    /** The order number as the register shows it ("17"). */
    orderNumber: z.string(),
    status: z.enum(['no_refund', 'partial', 'full', 'refunded']),
    /** Σ what the payments have left to give back (the Select Items step's "Refundable"). */
    refundable: Money,
    /** Which Refund Type tiles show. */
    options: z.object({ order: z.boolean(), items: z.boolean(), tips: z.boolean() }),
    /**
     * The Select Items rows, as legacy's refund suggestion (PR-150): a line, or one row per unit of a line of several
     * units (`rowId` `<line id>-<n>`; absent from nodes that answered one row per line).
     */
    lines: z.array(z.object({
      rowId: z.string().max(80).optional(), lineId: OrderLineId, name: z.string(), qtyMilli: Milli, refundable: Money, refunded: z.boolean(), request: RefundLine,
    })).max(5000),
    payments: z.array(z.object({
      tenderId: TenderId,
      method: TenderMethod,
      /** "Cash", the card's brand, "Check", "House Account" or the tender type's name. */
      label: z.string(),
      last4: z.string().max(4).nullable(),
      /** What it took as legacy shows it (`tenders.amount`): a Non-Cash Adjustment card's charge less its fee. */
      applied: Money,
      /**
       * The whole charge, an NCA card's fee inside it: what a void reverses at the processor (#1779). Absent from a node
       * before it, whose `applied` was the whole charge.
       */
      charged: Money.optional(),
      tip: Money,
      amountLeft: Money,
      tipLeft: Money,
      /**
       * What a card's refund goes back by at the processor (its sale's or capture's reference; an imported card's
       * legacy `transaction_id2 ?? transaction_id`), as the register already holds it for its own cards. Never the card
       * token. Null for a card the processor cannot be asked about (an imported card legacy kept no reference for).
       */
      processorRef: z.string().max(128).nullable(),
      /**
       * B1563: an online order's card (recorded by `payments.recordOnlineTender`), refunded on the online store's gateway
       * through the cloud's `online.refundSale`, never the till's processor. Absent from a node before it.
       */
      onlineOrderId: z.uuid().optional(),
      ...PaymentScreenName,
    })).max(20),
    /**
     * B1706: cards whose capture was declined, their authorization still held: Void Order releases each at the processor
     * by `processorRef` (for `charged`, the whole charge) and voids it, as legacy voids every tender. Absent from a node
     * before it.
     */
    declined: z.array(z.object({
      tenderId: TenderId,
      label: z.string(),
      last4: z.string().max(4).nullable(),
      charged: Money,
      processorRef: z.string().max(128).nullable(),
      ...PaymentScreenName,
    })).max(20).optional(),
    /** Cards still awaiting their tip and capture (refunded only once finalized on the register that holds them). */
    toFinalize: z.array(TenderId).max(20),
    /**
     * B1936: the cards of `toFinalize` a register can finalize through the node (`payments.adjustTipAtRegister`, then
     * `payments.requestCaptureAtRegister`), with what Confirm tip shows and the capture sends. An imported card is never one (the import brings it captured or voided).
     * Absent from a node before it: the register then keeps its "confirm on the register that took it" note.
     */
    finalize: z.array(z.object({
      tenderId: TenderId,
      last4: z.string().max(4).nullable(),
      /**
       * What the card took, ex tip, a Non-Cash Adjustment fee inside it (the tender's `applied`, which the capture sends);
       * Confirm tip's "Payment amount" is it less `surcharge` (B1618).
       */
      applied: Money,
      /** The Non-Cash Adjustment fee inside `applied`, when the card carries one. */
      surcharge: Money.optional(),
      /** The tip saved on the card; null when none was entered yet ("Tip not yet entered"). */
      tip: Money.nullable(),
      status: z.enum(['authorized', 'charged', 'capture_pending', 'capture_failed']),
      /** The sale's reference the capture goes by at the processor. */
      processorRef: z.string().max(128),
    })).max(20).optional(),
    unconfirmed: z.array(UnconfirmedRefundLeg).max(20),
  })
  .meta({ id: 'RefundableOrder' });

export const RefundableOf = defineQuery({
  name: 'payments.refundableOf',
  summary: 'What a closed order the register does not hold can still give back: its items, its payments with what each has left, and refunds still waiting for a card answer.',
  input: z.object({ orderId: OrderId }),
  output: RefundableOrder,
  permission: { session: 'device' },
  availability: 'location',
  scope: 'location',
  errors: [RefundRuleViolation],
  agent: { expose: false },
  legacy: ['GET /v1/orders/{id}'],
});

const atRegister = {
  availability: 'location', origin: 'any', stream: { aggregate: 'refund', id: (input: { refundId: string }) => input.refundId },
  permission: { session: 'device' }, output: RefundSnapshot, agent: { expose: false },
} as const;

/** The employee acting at the register, and the manager who approved with their PIN when the employee could not. */
const ActingAtRegister = { employeeId: EmployeeId, approvedByEmployeeId: EmployeeId.optional() };

export const RequestRefundAtRegister = defineCommand({
  ...atRegister,
  name: 'payments.requestRefundAtRegister',
  risk: 'medium',
  dryRun: true,
  summary: 'payments.requestRefund for an order this register does not hold, decided by the node. The acting employee (or approving manager) needs the void permission.',
  input: RequestRefund.input.extend(ActingAtRegister),
  emits: [RefundRequested],
  errors: [RefundRuleViolation],
  legacy: ['POST /v1/refunds/{order_id}', 'POST /v1/order_refund/{order_id}'],
});

export const RecordRefundTenderResultAtRegister = defineCommand({
  ...atRegister,
  name: 'payments.recordRefundTenderResultAtRegister',
  risk: 'low',
  dryRun: false,
  summary: 'payments.recordRefundTenderResult for a refund the node decided: what the processor (or the drawer) did with one payment.',
  input: RecordRefundTenderResult.input.extend(ActingAtRegister),
  emits: [RefundTenderIssued, RefundTenderFailed, RefundCompleted],
  errors: [RefundRuleViolation],
});

export const SettleRefundTenderAtRegister = defineCommand({
  ...atRegister,
  name: 'payments.settleRefundTenderAtRegister',
  risk: 'high',
  dryRun: true,
  summary: 'payments.settleRefundTender for a refund the node decided. The acting employee (or approving manager) needs the void permission.',
  input: SettleRefundTenderInput.extend(ActingAtRegister),
  emits: [RefundTenderIssued, RefundTenderFailed, RefundCompleted],
  errors: [RefundRuleViolation],
});

export const refundCommands = [
  RequestRefund, RecordRefundTenderResult, RequestRefundAtRegister, RecordRefundTenderResultAtRegister, SettleRefundTender, SettleRefundTenderAtRegister,
] as const;
export const refundQueries = [RefundableOf] as const;
